import { useState, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/CreateEventModal.css";

const API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;

export default function LocationPicker({ initialLocation, onSelect }) {
  const [query, setQuery] = useState(
    initialLocation?.address || initialLocation?.venue_name || ""
  );
  const [results, setResults] = useState([]);
  const [suppressAutocomplete, setSuppressAutocomplete] = useState(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapIdRef = useRef(null);

  if (!mapIdRef.current) {
    mapIdRef.current = `map-${Math.random().toString(36).slice(2, 9)}`;
  }
  const mapId = mapIdRef.current;

  const eventIconRef = useRef(
    L.divIcon({
      className: "event-marker",
      html: `<div class="pulse"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    })
  );

  // pomocnicza: postaw marker i podłącz dragend
  const placeMarker = (lat, lng) => {
    if (!mapRef.current) return;

    if (markerRef.current) {
      markerRef.current.remove();
    }

    const m = L.marker([lat, lng], {
      draggable: true,
      icon: eventIconRef.current,
    }).addTo(mapRef.current);

    markerRef.current = m;

    m.on("dragend", async (ev) => {
      const pos = ev.target.getLatLng();
      await updateAddressFromCoords(pos.lat, pos.lng);
    });

    mapRef.current.flyTo([lat, lng], 15);
  };

  // reverse geocoding → update input + parent
  async function updateAddressFromCoords(lat, lng) {
    try {
      const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&lang=pl&apiKey=${API_KEY}`;
      const resp = await fetch(url);
      const data = await resp.json();

      if (!data.features?.length) return;

      const place = data.features[0].properties;

      const formatted =
        place.formatted ||
        [place.street, place.housenumber, place.city]
          .filter(Boolean)
          .join(" ");

      setSuppressAutocomplete(true);
      setQuery(formatted);
      setResults([]);

      onSelect({
        venue_name: place.name || formatted,
        address: formatted,
        city: place.city || "",
        country: place.country || "",
        lat,
        lng,
      });
    } catch (err) {
      console.error("reverse geocoding error", err);
    }
  }

  // autocomplete – tylko przy "prawdziwym" pisaniu
  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }

    if (suppressAutocomplete) {
      // ta zmiana query była programowa (reverse / wybór z listy) → nie strzelamy zapytania
      setSuppressAutocomplete(false);
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
            query
          )}&lang=pl&filter=countrycode:pl&apiKey=${API_KEY}`,
          { signal: controller.signal }
        );
        const data = await res.json();
        setResults(data.features || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("autocomplete error", err);
        }
      }
    };

    fetchData();

    return () => controller.abort();
  }, [query, suppressAutocomplete]);

  // inicjalizacja mapy (raz)
  useEffect(() => {
    const mapInstance = L.map(mapId, {
      center: [52.2297, 21.0122],
      zoom: 12,
    });

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      { maxZoom: 19 }
    ).addTo(mapInstance);

    mapRef.current = mapInstance;

    // double-click → wybór miejsca na mapie
    mapInstance.on("dblclick", async (e) => {
      const { lat, lng } = e.latlng;
      placeMarker(lat, lng);
      await updateAddressFromCoords(lat, lng);
    });

    return () => {
      mapInstance.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [mapId]);

  // ustawienie startowej lokalizacji przy edycji
  useEffect(() => {
    if (!mapRef.current) return;
    if (!initialLocation?.lat || !initialLocation?.lng) return;

    const { lat, lng, address, venue_name } = initialLocation;

    placeMarker(lat, lng);

    const label = address || venue_name || "";
    if (label) {
      setSuppressAutocomplete(true);
      setQuery(label);
      setResults([]);
    }
    // ❗ tutaj JUŻ NIE wołamy onSelect – rodzic te dane już ma
  }, [initialLocation?.lat, initialLocation?.lng]); // tylko gdy zmienia się pozycja

  const pickLocation = (place) => {
    if (!mapRef.current) return;

    const [lng, lat] = place.geometry.coordinates;
    placeMarker(lat, lng);

    const props = place.properties || {};
    const formatted = props.formatted || "";

    setSuppressAutocomplete(true);
    setQuery(formatted);
    setResults([]);

    onSelect({
      venue_name: props.name || formatted,
      address: formatted,
      city: props.city || "",
      country: props.country || "",
      lat,
      lng,
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <input
        className="form-control mb-2"
        placeholder="Szukaj miejsca..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {results.length > 0 && (
        <ul
          className="list-group mb-2"
          style={{ maxHeight: "150px", overflow: "auto" }}
        >
          {results.map((p) => (
            <li
              key={p.properties.place_id}
              className="list-group-item list-group-item-action"
              onClick={() => pickLocation(p)}
              style={{ cursor: "pointer" }}
            >
              {p.properties.formatted}
            </li>
          ))}
        </ul>
      )}

      <div
        id={mapId}
        style={{ height: "300px", borderRadius: "10px" }}
      />
    </div>
  );
}
